import React, { useState, useEffect, useReducer, useContext, useRef } from "react";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import useTicketsListCustomSocket from "./useTicketsListCustomSocket";
import { hasAutomation } from "../../helpers/ticketAutomation";
import { SiblingTicketsProvider } from "../../context/SiblingTickets/SiblingTicketsContext";

const useStyles = makeStyles((theme) => ({
  ticketsListWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    minHeight: 0, // Importante para flexbox
  },

  ticketsList: {
    flex: 1,
    maxHeight: "100%",
    overflowY: "auto", // Cambiado de "scroll" a "auto" para mejor comportamiento
    overflowX: "hidden", // Evitar scroll horizontal
    ...theme.scrollbarStyles,
    borderTop: "2px solid rgba(0, 0, 0, 0.12)",
    minHeight: 0, // Importante para flexbox
  },

  ticketsListHeader: {
    color: "rgb(67, 83, 105)",
    zIndex: 2,
    backgroundColor: "white",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  ticketsCount: {
    fontWeight: "normal",
    color: "rgb(104, 121, 146)",
    marginLeft: "8px",
    fontSize: "14px",
  },

  noTicketsText: {
    textAlign: "center",
    color: "rgb(104, 121, 146)",
    fontSize: "14px",
    lineHeight: "1.4",
  },

  noTicketsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    margin: "0px",
  },

  noTicketsDiv: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },

  // ✅ ESTILOS PARA EL BUSCADOR
  searchInputWrapper: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
    padding: "8px 12px",
    margin: "8px",
    border: "1px solid #e0e0e0",
    transition: "all 0.3s ease",
    "&:focus-within": {
      borderColor: theme.palette.primary.main,
      backgroundColor: "#fff",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },
  },

  searchIcon: {
    color: "#666",
    marginRight: "8px",
    fontSize: "20px",
  },

  searchInput: {
    flex: 1,
    fontSize: "14px",
    "& input": {
      padding: "4px 0",
      fontSize: "14px",
    },
    "& input::placeholder": {
      color: "#999",
      fontSize: "14px",
    },
  },

  searchSwitch: {
    marginLeft: "8px",
    "& .MuiSwitch-thumb": {
      width: "16px",
      height: "16px",
    },
    "& .MuiSwitch-track": {
      height: "12px",
      borderRadius: "6px",
    },
  },

  // ✅ ESTILOS PARA LA BARRA DE HERRAMIENTAS
  quickActionsBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 16px",
    backgroundColor: "#f8f9fa",
    borderBottom: "1px solid #e0e0e0",
    minHeight: "56px",
  },

  quickActionsLeft: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  quickActionsRight: {
    display: "flex",
    alignItems: "center",
  },

  quickActionButton: {
    minWidth: "40px",
    height: "40px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
    backgroundColor: "#fff",
    color: "#666",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#f5f5f5",
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-selected": {
      backgroundColor: theme.palette.primary.main,
      color: "#fff",
      borderColor: theme.palette.primary.main,
    },
  },

  quickActionIconButton: {
    minWidth: "40px",
    height: "40px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
    backgroundColor: "#fff",
    color: "#666",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#f5f5f5",
      borderColor: theme.palette.primary.main,
    },
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_TICKETS") {
    const newTickets = action.payload;

    // ✅ DEBUG logs removidos para producción

    newTickets.forEach((ticket) => {
      const ticketIndex = state.findIndex((t) => t.id === ticket.id);
      if (ticketIndex !== -1) {
        state[ticketIndex] = ticket;
        if (ticket.unreadMessages > 0) {
          state.unshift(state.splice(ticketIndex, 1)[0]);
        }
      } else {
        state.push(ticket);
      }
    });

    return [...state];
  }

  if (action.type === "ADD_TICKET") {
    const ticket = action.payload;
    
    // ✅ VERIFICAR SI EL TICKET YA EXISTE PARA EVITAR DUPLICADOS
    const existingTicketIndex = state.findIndex((t) => t.id === ticket.id);
    if (existingTicketIndex !== -1) {
      // ✅ SI YA EXISTE, ACTUALIZAR EN LUGAR DE DUPLICAR (usar misma lógica que UPDATE_TICKET)
      const newTags = Array.isArray(ticket.tags)
        ? ticket.tags.map(tag => ({ ...tag }))
        : (state[existingTicketIndex]?.tags || []); // ✅ A1: preservar tags si el payload no los trae

      state[existingTicketIndex] = {
        ...ticket,
        tags: newTags, // ✅ NUEVA REFERENCIA para forzar re-render
        contact: ticket.contact ? { ...ticket.contact } : ticket.contact,
        user: ticket.user ? { ...ticket.user } : ticket.user
      };
      return [...state];
    }
    
    // ✅ SI NO EXISTE, AGREGAR AL INICIO
    // ✅ Asegurar que tags tenga nueva referencia también al agregar
    const newTags = Array.isArray(ticket.tags) 
      ? ticket.tags.map(tag => ({ ...tag }))
      : (ticket.tags || []);
    
    const ticketWithNewRefs = {
      ...ticket,
      tags: newTags,
      contact: ticket.contact ? { ...ticket.contact } : ticket.contact,
      user: ticket.user ? { ...ticket.user } : ticket.user
    };
    
    return [ticketWithNewRefs, ...state];
  }

  if (action.type === "RESET_UNREAD") {
    const ticketId = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state[ticketIndex].unreadMessages = 0;
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET") {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    
    if (ticketIndex !== -1) {
      // ✅ FIX 26/12/2025: Crear nuevo objeto para forzar re-render de React
      // Asegurar que todas las propiedades relevantes sean nuevas referencias
      // ✅ CRÍTICO: Crear nueva referencia para tags para que React detecte el cambio
      const newTags = Array.isArray(ticket.tags)
        ? ticket.tags.map(tag => ({ ...tag })) // Crear nuevos objetos para cada tag
        : (state[ticketIndex]?.tags || []); // ✅ A1: preservar tags si el payload no los trae (markAsRead/reload sin Tag)
      
      // ✅ CRÍTICO: Usar updatedAt del ticket si viene del backend, sino usar fecha actual
      // El backend ahora actualiza updatedAt explícitamente, así que debemos respetarlo
      const ticketUpdatedAt = ticket.updatedAt ? new Date(ticket.updatedAt).toISOString() : new Date().toISOString();
      
      // 0424 F3: los payloads de socket NO siempre traen las relaciones. El evento de
      // mensaje manda el ticket sin `whatsapp`, y al reemplazar el objeto entero el chip
      // de la conexión desaparecía de la fila en cuanto entraba un mensaje. Con un solo
      // número apenas se notaba; con dos, el agente pierde justo el dato que necesita.
      // Se conserva lo anterior SOLO cuando el payload no lo trae — un valor explícito
      // del backend (incluido `null`) sigue mandando.
      const previo = state[ticketIndex] || {};
      const conservar = (clave) =>
        ticket[clave] !== undefined ? ticket[clave] : previo[clave];

      const updatedTicket = {
        ...ticket,
        tags: newTags, // ✅ NUEVA REFERENCIA para forzar re-render
        lastMessage: ticket.lastMessage ? (typeof ticket.lastMessage === 'string' ? ticket.lastMessage : { ...ticket.lastMessage }) : ticket.lastMessage,
        updatedAt: ticketUpdatedAt, // ✅ Usar updatedAt del backend (actualizado explícitamente)
        whatsapp: conservar("whatsapp"),
        whatsappId: conservar("whatsappId"),
        queue: conservar("queue"),
        // ✅ Asegurar que contact y user también sean nuevas referencias
        contact: ticket.contact ? { ...ticket.contact } : conservar("contact"),
        user: ticket.user ? { ...ticket.user } : conservar("user")
      };
      
      // ✅ LOG PARA DEBUG (solo si es necesario)
      // console.log(`[TicketsListCustom Reducer] 🔄 UPDATE_TICKET - Ticket ${ticket.id}:`, {
      //   oldTagsCount: state[ticketIndex].tags?.length || 0,
      //   newTagsCount: newTags.length,
      //   oldTags: state[ticketIndex].tags?.map(t => t.id) || [],
      //   newTags: newTags.map(t => t.id),
      //   updatedAt: ticketUpdatedAt
      // });
      
      state[ticketIndex] = updatedTicket;
    } else {
      state.unshift(ticket);
    }

    // ✅ REORDENAR POR updatedAt EN ORDEN DESCENDENTE
    const sortedTickets = [...state].sort((a, b) => {
      const dateA = new Date(a.updatedAt);
      const dateB = new Date(b.updatedAt);
      return dateB - dateA; // DESC: más reciente primero
    });
    
    return sortedTickets;
  }

  // ✅ AGREGADO: Actualizar solo las reacciones del ticket
  if (action.type === "UPDATE_TICKET_REACTIONS") {
    const { ticketId, lastMessageWithReactions } = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state[ticketIndex] = {
        ...state[ticketIndex],
        lastMessageWithReactions: lastMessageWithReactions
      };
    } 

    return [...state];
  }

  if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      state[ticketIndex] = ticket;
      state.unshift(state.splice(ticketIndex, 1)[0]);
    } else {
      state.unshift(ticket);
    }

    // ✅ REORDENAR POR updatedAt EN ORDEN DESCENDENTE
    const sortedTickets = [...state].sort((a, b) => {
      const dateA = new Date(a.updatedAt);
      const dateB = new Date(b.updatedAt);
      return dateB - dateA; // DESC: más reciente primero
    });
    
    return sortedTickets;
  }

  if (action.type === "UPDATE_TICKET_CONTACT") {
    const contact = action.payload;
    // ✅ FIX 05/12/2025: Buscar por contactId o contact.id como fallback
    const ticketIndex = state.findIndex((t) => 
      t.contactId === contact.id || t.contact?.id === contact.id
    );
    if (ticketIndex !== -1) {
      // ✅ FIX 05/12/2025: Crear nuevo objeto para forzar re-render de React
      state[ticketIndex] = {
        ...state[ticketIndex],
        contact: { ...contact } // Crear nuevo objeto contact
      };
    }
    return [...state];
  }

  if (action.type === "UPDATE_TICKET_PRESENCE") {
    const data = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === data.ticketId);
    if (ticketIndex !== -1) {
      state[ticketIndex] = { ...state[ticketIndex], presence: data.presence };
    }
    return [...state];
  }

  if (action.type === "DELETE_TICKET") {
    const ticketId = action.payload;
    // ✅ LOGS REMOVIDOS PARA USUARIO FINAL
    
    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      // ✅ DELETE_TICKET reducer: Ticket eliminado
      state.splice(ticketIndex, 1);
    } else {
      // ✅ LOGS REMOVIDOS PARA USUARIO FINAL
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const TicketsListCustom = (props) => {
  // ✅ TODOS LOS HOOKS DEBEN IR AL INICIO - SIN EXCEPCIONES
  const {
    status,
    searchParam,
    tags,
    users,
    showAll,
    selectedQueueIds,
    updateCount,
    style,
    aiAgentFilter, // ✅ NUEVO PROP PARA FILTRAR AGENTES IA
    excludeAIAgents, // ✅ NUEVO PROP PARA EXCLUIR AGENTES IA
    searchOnMessages, // ✅ NUEVO PROP PARA BUSCAR EN MENSAJES
    isActiveTab = true, // ✅ NUEVO PROP PARA INDICAR SI ES LA PESTAÑA ACTIVA
    sortAscending = false, // ✅ NUEVO PROP PARA ORDENAMIENTO
  } = props || {};
  const classes = useStyles();
  const [pageNumber, setPageNumber] = useState(1);
  const [ticketsList, dispatch] = useReducer(reducer, []);
  // Mantener una referencia siempre actualizada de la lista para usarla en callbacks de socket
  const ticketsListRef = useRef([]);
  useEffect(() => {
    ticketsListRef.current = ticketsList;
  }, [ticketsList]);
  const { user } = useContext(AuthContext);
  const { profile, queues } = user || {};
  const isMounted = useRef(true);
  const socketManager = useContext(SocketContext);
  
  // ✅ MOVER TODOS LOS HOOKS AL INICIO - ANTES DE CUALQUIER VERIFICACIÓN CONDICIONAL
  // ✅ Normalizar selectedQueueIds para evitar reseteos innecesarios
  const normalizedQueueIds = selectedQueueIds && selectedQueueIds.length > 0 ? selectedQueueIds : null;
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [status, searchParam, dispatch, showAll, tags, users, normalizedQueueIds, aiAgentFilter, excludeAIAgents]); // ✅ Solo resetear si hay filtros activos


  const { tickets, hasMore, loading } = useTickets({
    pageNumber,
    searchParam: isActiveTab ? searchParam : "", // ✅ Solo buscar si es la pestaña activa
    status,
    showAll,
    tags: JSON.stringify(tags),
    users: JSON.stringify(users),
    queueIds: selectedQueueIds && selectedQueueIds.length > 0 ? JSON.stringify(selectedQueueIds) : undefined, // ✅ Solo pasar queueIds si hay filtros activos
    searchOnMessages: searchOnMessages,
    sortAscending: sortAscending, // ✅ AGREGADO: Parámetro de ordenamiento
  });

  useEffect(() => {
    // El backend (ListTicketsService) es la fuente de verdad para visibilidad por rol.
    // Solo aplicamos filtros de tipo de ticket (AI / no-AI) que no tienen equivalente en HTTP.
    let filteredTickets = tickets;

    if (aiAgentFilter) {
      filteredTickets = filteredTickets.filter(ticket => isAIAgent(ticket));
    }
    if (excludeAIAgents) {
      filteredTickets = filteredTickets.filter(ticket => !isAIAgent(ticket));
    }

    dispatch({ type: "LOAD_TICKETS", payload: filteredTickets });
  }, [tickets, status, searchParam, aiAgentFilter, excludeAIAgents]); // eslint-disable-line react-hooks/exhaustive-deps

  // Limpieza reactiva: elimina tickets que llegaron por socket a un tab equivocado.
  // No filtra por cola/usuario — eso es responsabilidad del backend (crm-roles).
  useEffect(() => {
    if (!ticketsList || ticketsList.length === 0 || status === "all") return;

    const ticketsToRemove = ticketsList
      .filter(ticket => {
        const ticketHasAI = isAIAgent(ticket);
        const ticketStatus = ticket.status;
        const isGroupTicket = ticket.contact && ticket.contact.isGroup;

        if (status === "ai")    return !ticketHasAI;
        if (status === "group") return !isGroupTicket || ticketStatus === "closed";
        if (status === "closed") return ticketStatus !== "closed";
        // open / pending
        if (ticketHasAI || isGroupTicket) return true;
        return ticketStatus !== status;
      })
      .map(t => t.id);

    ticketsToRemove.forEach(id => dispatch({ type: "DELETE_TICKET", payload: id }));
  }, [ticketsList, status]); // eslint-disable-line react-hooks/exhaustive-deps


  
  // 0424 F0: la definicion vive en helpers/ticketAutomation — no se duplica aqui.
  const isAIAgent = hasAutomation;

  useTicketsListCustomSocket({
    status, showAll, user, selectedQueueIds, profile, queues,
    socketManager, excludeAIAgents, dispatch, isMounted, ticketsListRef,
  });

  useEffect(() => {
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    if (typeof updateCount === "function") {
      updateCount(ticketsList.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsList]);

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  // ✅ VERIFICACIONES DE SEGURIDAD DESPUÉS DE TODOS LOS HOOKS
  if (!props) {
    console.error("❌ TicketsListCustom: props es undefined");
    return null;
  }
  
  if (!user) {
    console.error("❌ TicketsListCustom: user es undefined");
    return null;
  }
  
  if (!queues || !Array.isArray(queues)) {
    console.error("❌ TicketsListCustom: queues es undefined o no es array:", queues);
    return null;
  }

  return (
    <Paper className={classes.ticketsListWrapper} style={style}>
      <Paper
        square
        name="closed"
        elevation={0}
        className={classes.ticketsList}
        onScroll={handleScroll}
      >
        <List style={{ paddingTop: 0 }}>
          {ticketsList.length === 0 && !loading ? (
            <div className={classes.noTicketsDiv}>
              <span className={classes.noTicketsTitle}>
                {i18n.t("ticketsList.noTicketsTitle")}
              </span>
              <p className={classes.noTicketsText}>
                {i18n.t("ticketsList.noTicketsMessage")}
              </p>
            </div>
          ) : (
            <SiblingTicketsProvider tickets={ticketsList}>
              {ticketsList.map((ticket) => (
                <TicketListItem 
                  ticket={ticket} 
                  status={status}
                  key={`${ticket.id}-${ticket.updatedAt || ticket.lastMessage?.id || Date.now()}`} 
                />
              ))}
            </SiblingTicketsProvider>
          )}
          {loading && <TicketsListSkeleton />}
        </List>
      </Paper>
    </Paper>
  );
};

export default TicketsListCustom;
